import { useMemo, useState } from 'react';
import { CriarVerbeteSchema, NovaVersaoVerbeteSchema, pastaCurta, type PropostaPendente, type VerbeteResumo } from '@remoto/shared';
import { CaixaDeMarcar, CampoDeEscolha } from '../componentes/campos';
import { Modal, usarConfirmacao } from '../componentes/Modal';
import { IconeBuscar, IconeMais } from '../componentes/icones';
import { TextoMarkdown } from '../componentes/chat/TextoMarkdown';
import { mensagemDoErro } from '../erros';
import { GESTOS, usarAlvos, usarGesto, usarPropostas, usarRotinas, usarVerbetes } from './consultas';
import { CartaoDeProposta } from './CartaoDeProposta';
import {
  AvisoDeErro,
  BotaoPrimario,
  BotaoSecundario,
  CampoDeLinha,
  CampoDeTextoLongo,
  usarPodeComandar,
} from './pecas';
import { CLASSES_DO_VERBETE, filtrarVerbetes, ROTULO_DA_CLASSE_DO_VERBETE, validar, type ClasseDoVerbete } from './regras';
import { SeloNeutro } from './selos';

/**
 * A aba Dicionário: os "jeitos de fazer" em texto, classificados (F3.1), com
 * as propostas de verbete da mestre no alto — aprovar e recusar aqui mesmo,
 * no cartão de rodapé fixo.
 *
 * O número de um verbete é ESTÁVEL (R22): é por ele que se cita, e a tela o
 * mostra na frente do termo.
 */

const TODAS = '*';

/** Marcar vários de uma lista curta (alvos, rotinas) com a caixa da casa. */
function EscolhaMultipla({
  rotulo,
  itens,
  marcados,
  aoMudar,
}: {
  rotulo: string;
  itens: { id: string; rotulo: string }[];
  marcados: string[];
  aoMudar: (marcados: string[]) => void;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-sm text-zinc-400">{rotulo}</legend>
      {itens.length === 0 ? (
        <p className="text-xs text-zinc-500">nenhum cadastrado</p>
      ) : (
        <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-zinc-800 p-2">
          {itens.map((item) => (
            <CaixaDeMarcar
              key={item.id}
              marcada={marcados.includes(item.id)}
              aoMarcar={(m) => aoMudar(m ? [...marcados, item.id] : marcados.filter((x) => x !== item.id))}
              className="text-xs text-zinc-300"
            >
              <span className="font-mono">{item.rotulo}</span>
            </CaixaDeMarcar>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/**
 * Criar um verbete ou escrever a versão nova de um (humano pela tela: aprovado
 * direto, D19). A data da ORDEM é texto `AAAA-MM-DD`: o `<input type="date">`
 * cru é proibido (`web-controles.md`), e o formato é o que o contrato aceita.
 */
function DialogoDoVerbete({
  aberto,
  aoFechar,
  existente,
}: {
  aberto: boolean;
  aoFechar: () => void;
  /** Presente: versão nova deste verbete. Ausente: verbete novo. */
  existente: VerbeteResumo | null;
}): React.JSX.Element | null {
  const alvos = usarAlvos();
  const rotinas = usarRotinas(false);
  const criar = usarGesto(GESTOS.criarVerbete);
  const novaVersao = usarGesto(GESTOS.novaVersaoVerbete);
  const [termo, setTermo] = useState('');
  const [classe, setClasse] = useState<ClasseDoVerbete | null>(existente?.classe ?? null);
  const [texto, setTexto] = useState(existente?.vigente?.texto ?? '');
  const [alvosMarcados, setAlvos] = useState<string[]>(existente?.vigente?.alvos ?? []);
  const [rotinasMarcadas, setRotinas] = useState<string[]>(existente?.vigente?.rotinas ?? []);
  const [autor, setAutor] = useState(existente?.vigente?.autorDaOrdem ?? '');
  const [data, setData] = useState(existente?.vigente?.dataDaOrdem ?? '');
  const [motivo, setMotivo] = useState('');
  const [problemas, setProblemas] = useState<string[]>([]);
  const ehOrdem = (existente?.classe ?? classe) === 'ORDEM';
  const ocupado = criar.isPending || novaVersao.isPending;
  const erro = criar.error ?? novaVersao.error;

  function gravar(): void {
    const comum = {
      texto,
      alvos: alvosMarcados,
      rotinas: rotinasMarcadas,
      motivo,
      ...(ehOrdem && autor.trim() ? { autorDaOrdem: autor.trim() } : {}),
      ...(ehOrdem && data.trim() ? { dataDaOrdem: data.trim() } : {}),
    };
    if (existente) {
      const r = validar(NovaVersaoVerbeteSchema, { ...comum, ...(existente.vigente ? { substituiId: existente.vigente.id } : {}) });
      if (!r.ok) return setProblemas(r.problemas);
      setProblemas([]);
      novaVersao.mutate({ id: existente.id, corpo: r.dados }, { onSuccess: aoFechar });
      return;
    }
    const r = validar(CriarVerbeteSchema, { ...comum, termo, classe: classe ?? undefined });
    if (!r.ok) return setProblemas(r.problemas);
    setProblemas([]);
    criar.mutate(r.dados, { onSuccess: aoFechar });
  }

  return (
    <Modal
      titulo={existente ? `Nova versão do verbete nº${existente.numero}` : 'Novo verbete'}
      aberto={aberto}
      aoFechar={aoFechar}
      rodape={
        <>
          <BotaoSecundario onClick={aoFechar}>Cancelar</BotaoSecundario>
          <BotaoPrimario disabled={ocupado} onClick={gravar}>
            {existente ? 'Gravar versão' : 'Criar verbete'}
          </BotaoPrimario>
        </>
      }
    >
      <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
        <p className="text-xs text-zinc-500">Pela tela, a versão nasce aprovada por você.</p>
        {existente ? null : (
          <>
            <CampoDeLinha rotulo="Termo" value={termo} onChange={(e) => setTermo(e.target.value)} maxLength={200} />
            <CampoDeEscolha
              rotulo="Classe"
              valor={classe}
              opcoes={CLASSES_DO_VERBETE.map((c) => ({ id: c, rotulo: ROTULO_DA_CLASSE_DO_VERBETE[c] }))}
              aoEscolher={setClasse}
            />
          </>
        )}
        <CampoDeTextoLongo rotulo="Texto (markdown)" value={texto} onChange={(e) => setTexto(e.target.value)} rows={6} />
        {ehOrdem ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <CampoDeLinha rotulo="Quem deu a ordem" value={autor} onChange={(e) => setAutor(e.target.value)} maxLength={200} />
            <CampoDeLinha
              rotulo="Data da ordem"
              value={data}
              onChange={(e) => setData(e.target.value)}
              placeholder="AAAA-MM-DD"
              inputMode="numeric"
            />
          </div>
        ) : null}
        <EscolhaMultipla
          rotulo="Alvos"
          itens={(alvos.data ?? []).map((a) => ({ id: a.chave, rotulo: a.chave }))}
          marcados={alvosMarcados}
          aoMudar={setAlvos}
        />
        <EscolhaMultipla
          rotulo="Rotinas ligadas"
          itens={(rotinas.data ?? []).map((r) => ({ id: r.nome, rotulo: r.nome }))}
          marcados={rotinasMarcadas}
          aoMudar={setRotinas}
        />
        <CampoDeLinha rotulo="Motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={2_000} />
        {problemas.length > 0 ? (
          <AvisoDeErro>
            {problemas.map((p) => (
              <span key={p} className="block">
                {p}
              </span>
            ))}
          </AvisoDeErro>
        ) : null}
        {erro ? <AvisoDeErro>{mensagemDoErro(erro, 'Não foi possível gravar o verbete agora.')}</AvisoDeErro> : null}
      </div>
    </Modal>
  );
}

function LinhaDoVerbete({
  verbete,
  podeComandar,
  aoEditar,
}: {
  verbete: VerbeteResumo;
  podeComandar: boolean;
  aoEditar: () => void;
}): React.JSX.Element {
  const [inteiro, setInteiro] = useState(false);
  const arquivar = usarGesto(GESTOS.arquivarVerbete);
  const [pedir, dialogo] = usarConfirmacao();
  const v = verbete.vigente;
  return (
    <li data-testid="verbete" className="min-w-0 rounded-lg border border-zinc-800 p-3">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <span className="font-mono text-xs text-zinc-500">nº{verbete.numero}</span>
        <span className="min-w-0 break-words text-sm font-medium text-zinc-100">{verbete.termo}</span>
        <SeloNeutro>{ROTULO_DA_CLASSE_DO_VERBETE[verbete.classe]}</SeloNeutro>
        {verbete.propostasPendentes > 0 ? <SeloNeutro tom="azul">proposta esperando decisão</SeloNeutro> : null}
        {verbete.arquivado ? <SeloNeutro>arquivado</SeloNeutro> : null}
        {verbete.projeto ? (
          <span className="text-xs text-zinc-500" title={verbete.projeto.cwdProjeto}>
            projeto {pastaCurta(verbete.projeto.cwdProjeto)}
          </span>
        ) : null}
      </div>
      {v ? (
        <>
          <div className={'mt-2 text-sm ' + (inteiro ? '' : 'max-h-24 overflow-hidden')}>
            <TextoMarkdown texto={v.texto} />
          </div>
          <button
            type="button"
            onClick={() => setInteiro((x) => !x)}
            className="mt-1 text-xs text-zinc-500 underline-offset-2 hover:text-zinc-300 hover:underline pointer-coarse:min-h-11"
          >
            {inteiro ? 'mostrar menos' : 'ler inteiro'}
          </button>
          {v.autorDaOrdem || v.dataDaOrdem ? (
            <p className="mt-1 text-xs text-zinc-400">
              ordem{v.autorDaOrdem ? ` de ${v.autorDaOrdem}` : ''}
              {v.dataDaOrdem ? ` em ${v.dataDaOrdem.split('-').reverse().join('/')}` : ''}
            </p>
          ) : null}
          {v.alvos.length > 0 || v.rotinas.length > 0 ? (
            <p className="mt-1 break-words text-xs text-zinc-500">
              {v.alvos.length > 0 ? <>alvos: <span className="font-mono">{v.alvos.join(', ')}</span> </> : null}
              {v.rotinas.length > 0 ? <>· rotinas: <span className="font-mono">{v.rotinas.join(', ')}</span></> : null}
            </p>
          ) : null}
        </>
      ) : (
        <p className="mt-2 text-xs text-zinc-500">Ainda sem versão aprovada.</p>
      )}
      {podeComandar ? (
        <div className="mt-2 flex flex-wrap gap-2">
          {!verbete.arquivado ? <BotaoSecundario onClick={aoEditar} className="text-xs">Nova versão</BotaoSecundario> : null}
          <BotaoSecundario
            className="text-xs"
            disabled={arquivar.isPending}
            onClick={() =>
              pedir({
                titulo: verbete.arquivado ? 'Desarquivar o verbete' : 'Arquivar o verbete',
                texto: verbete.arquivado
                  ? 'O verbete volta a aparecer para as sessões.'
                  : 'O verbete sai da busca das sessões. O número e o histórico ficam.',
                rotuloConfirmar: verbete.arquivado ? 'Desarquivar' : 'Arquivar',
                aoConfirmar: () => arquivar.mutate({ id: verbete.id, arquivado: !verbete.arquivado }),
              })
            }
          >
            {verbete.arquivado ? 'Desarquivar' : 'Arquivar'}
          </BotaoSecundario>
        </div>
      ) : null}
      {arquivar.error ? <AvisoDeErro>{mensagemDoErro(arquivar.error, 'Não foi possível arquivar agora.')}</AvisoDeErro> : null}
      {dialogo}
    </li>
  );
}

export function AbaDicionario(): React.JSX.Element {
  const podeComandar = usarPodeComandar();
  const [arquivados, setArquivados] = useState(false);
  const [texto, setTexto] = useState('');
  const [classe, setClasse] = useState<ClasseDoVerbete | null>(null);
  const [dialogo, setDialogo] = useState<{ existente: VerbeteResumo | null } | null>(null);
  const verbetes = usarVerbetes(arquivados);
  const propostas = usarPropostas();
  const daFila = (propostas.data ?? []).filter((p): p is Extract<PropostaPendente, { especie: 'verbete' }> => p.especie === 'verbete');
  const visiveis = useMemo(() => filtrarVerbetes(verbetes.data ?? [], { texto, classe }), [verbetes.data, texto, classe]);

  return (
    <div className="space-y-4">
      {daFila.length > 0 ? (
        <section aria-label="Verbetes esperando decisão" className="space-y-3">
          <h2 className="text-sm font-medium text-sky-100">Esperando decisão ({daFila.length})</h2>
          {daFila.map((p) => (
            <CartaoDeProposta key={p.id} proposta={p} />
          ))}
        </section>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-0 flex-1 basis-48 items-center gap-2 rounded-md border border-zinc-700 bg-zinc-950 px-2.5 py-1.5 focus-within:border-zinc-500">
          <IconeBuscar tamanho={14} className="text-zinc-500" />
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            placeholder="Buscar no dicionário"
            aria-label="Buscar no dicionário"
            className="min-w-0 flex-1 bg-transparent text-sm text-zinc-100 outline-none placeholder:text-zinc-600 pointer-coarse:text-base"
          />
        </label>
        <CampoDeEscolha
          rotulo="Classe do verbete"
          rotuloOculto
          compacto
          valor={classe ?? TODAS}
          opcoes={[
            { id: TODAS, rotulo: 'todas as classes' },
            ...CLASSES_DO_VERBETE.map((c) => ({ id: c as string, rotulo: ROTULO_DA_CLASSE_DO_VERBETE[c] })),
          ]}
          aoEscolher={(v) => setClasse(v === TODAS ? null : (v as ClasseDoVerbete))}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <CaixaDeMarcar marcada={arquivados} aoMarcar={setArquivados} className="text-xs text-zinc-400">
          mostrar arquivados
        </CaixaDeMarcar>
        {podeComandar ? (
          <BotaoPrimario onClick={() => setDialogo({ existente: null })}>
            <IconeMais tamanho={14} />
            Novo verbete
          </BotaoPrimario>
        ) : null}
      </div>

      {verbetes.isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {verbetes.error ? <AvisoDeErro>{mensagemDoErro(verbetes.error, 'Não foi possível carregar o dicionário agora.')}</AvisoDeErro> : null}
      {verbetes.data && visiveis.length === 0 ? (
        <p className="text-sm text-zinc-500">{verbetes.data.length === 0 ? 'O dicionário ainda está vazio.' : 'Nenhum verbete com estes filtros.'}</p>
      ) : null}
      {visiveis.length > 0 ? (
        <ul data-testid="lista-de-verbetes" className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {visiveis.map((v) => (
            <LinhaDoVerbete key={v.id} verbete={v} podeComandar={podeComandar} aoEditar={() => setDialogo({ existente: v })} />
          ))}
        </ul>
      ) : null}

      {dialogo ? (
        <DialogoDoVerbete
          // A chave refaz o formulário a cada abertura: o rascunho de um verbete não vaza para outro.
          key={dialogo.existente?.id ?? 'novo'}
          aberto
          existente={dialogo.existente}
          aoFechar={() => setDialogo(null)}
        />
      ) : null}
    </div>
  );
}
